/**
 * src/engine/solver.ts — goal-seek via binary search over a monotonic input (§6.16).
 * Each solver returns the solved value and the binding constraint.
 */
import type { ScenarioInputs } from '@/types';
import { runProjection } from './projection';

export type SolverMode = 'max_draw' | 'max_initial_borrow' | 'required_return';

export interface SolverResult {
  mode: SolverMode;
  value: number;
  bindingConstraint: string;
}

const DEFAULT_TOL = 1e-3;

/** Largest x in [lo, hi] for which `pred(x)` holds (pred true below, false above). */
function largestSatisfying(pred: (x: number) => boolean, lo: number, hi: number, tol: number): number {
  if (!pred(lo)) return lo; // even the minimum violates → return the floor
  let a = lo;
  let b = hi;
  while (b - a > tol) {
    const mid = (a + b) / 2;
    if (pred(mid)) a = mid;
    else b = mid;
  }
  return a;
}

/** Smallest x in [lo, hi] for which `pred(x)` holds (pred false below, true above). */
function smallestSatisfying(pred: (x: number) => boolean, lo: number, hi: number, tol: number): number {
  if (pred(lo)) return lo;
  let a = lo;
  let b = hi;
  while (b - a > tol) {
    const mid = (a + b) / 2;
    if (pred(mid)) b = mid;
    else a = mid;
  }
  return b;
}

function worstLtv(inputs: ScenarioInputs): number {
  return runProjection(inputs).summary.worstLtv;
}

/** Max sustainable monthly draw s.t. worst-case LTV ≤ targetLtv (§6.16). */
export function solveMaxDraw(
  inputs: ScenarioInputs,
  targetLtv: number,
  tol = DEFAULT_TOL,
): SolverResult {
  const hi = Math.max(inputs.portfolioValue, 1); // generous upper bound
  const pred = (x: number) => worstLtv({ ...inputs, drawAmount: x }) <= targetLtv;
  const value = largestSatisfying(pred, 0, hi, tol);
  return { mode: 'max_draw', value, bindingConstraint: `worst-case LTV ≤ ${(targetLtv * 100).toFixed(0)}%` };
}

/** Max initial borrow s.t. worst-case LTV ≤ targetLtv (§6.16). */
export function solveMaxInitialBorrow(
  inputs: ScenarioInputs,
  targetLtv: number,
  tol = DEFAULT_TOL,
): SolverResult {
  const hi = Math.max(inputs.portfolioValue * inputs.blendedAdvanceRate, 1);
  const pred = (x: number) => worstLtv({ ...inputs, initialBalance: x }) <= targetLtv;
  const value = largestSatisfying(pred, 0, hi, tol);
  return {
    mode: 'max_initial_borrow',
    value,
    bindingConstraint: `worst-case LTV ≤ ${(targetLtv * 100).toFixed(0)}%`,
  };
}

/** Required portfolio return s.t. terminal net worth ≥ target (§6.16). */
export function solveRequiredReturn(
  inputs: ScenarioInputs,
  targetNetWorth: number,
  tol = DEFAULT_TOL,
): SolverResult {
  const pred = (x: number) =>
    runProjection({ ...inputs, portfolioExpectedReturnAnnualPct: x }).summary
      .endingNetWorth >= targetNetWorth;
  const value = smallestSatisfying(pred, -50, 50, tol);
  return {
    mode: 'required_return',
    value,
    bindingConstraint: `terminal net worth ≥ ${Math.round(targetNetWorth)}`,
  };
}
